import { hasSupabaseConfig, supabase } from '../../../shared/api/supabaseClient.js';
import { buildManualBossMaster } from '../domain/bossSeasonAssignment.js';
import {
  attachBossMasterDisplayImages,
  normalizeBossMasterCatalogRow,
} from '../domain/bossMasterCatalog.js';

const BOSS_MASTER_FUNCTION = 'sync-boss-master';

function requireClient() {
  if (!hasSupabaseConfig || !supabase) throw new Error('Supabase 환경 변수가 설정되지 않았습니다.');
  return supabase;
}

function formatFunctionError(payload, fallback = '') {
  const message = String(payload?.message || '').trim();
  const code = String(payload?.code || '').trim();
  if (message && code && !message.includes(code)) return `${message} (${code})`;
  return message || code || fallback;
}

async function readFunctionError(error) {
  try {
    const payload = await error?.context?.json();
    return formatFunctionError(payload, error?.message);
  } catch {
    return error?.message;
  }
}

function plainObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

export function compactBossMasterExport(input) {
  const payload = plainObject(input);
  const seasons = plainObject(payload.seasons);
  const bosses = Array.isArray(payload.bosses) ? payload.bosses : [];
  if (!Object.keys(seasons).length && !bosses.length) {
    throw new Error('보스 항목이 없는 JSON입니다.');
  }
  return {
    source: payload.source,
    version: payload.version,
    debugger: plainObject(payload.debugger),
    summary: plainObject(payload.summary),
    seasons,
    bosses,
    loaded_urls: Array.isArray(payload.loaded_urls) ? payload.loaded_urls : [],
    confirmed_or_strongly_inferred_mappings: Array.isArray(payload.confirmed_or_strongly_inferred_mappings)
      ? payload.confirmed_or_strongly_inferred_mappings
      : Array.isArray(payload.mappings) ? payload.mappings : [],
  };
}

async function invoke(body) {
  const { data, error } = await requireClient().functions.invoke(BOSS_MASTER_FUNCTION, { body });
  if (error) throw new Error(await readFunctionError(error) || '보스 DB 요청에 실패했습니다.');
  if (!data?.ok) throw new Error(formatFunctionError(data, '보스 DB 요청에 실패했습니다.'));
  return data;
}

export function loadBossMasterSyncStatus() {
  return invoke({ action: 'status' });
}

export function previewBossMasterExport(payload) {
  return invoke({ action: 'preview', payload: compactBossMasterExport(payload) });
}

export function applyBossMasterExport(payload) {
  return invoke({ action: 'apply', payload: compactBossMasterExport(payload) });
}

export function previewEnikkBossMasterCatalog() {
  return invoke({ action: 'enikk_preview' });
}

export function applyEnikkBossMasterCatalog() {
  return invoke({ action: 'enikk_apply' });
}

export async function loadBossMasterCatalog() {
  const client = requireClient();
  const [masterResult, legacyResult] = await Promise.all([
    client
      .from('boss_master')
      .select('*')
      .eq('is_visible', true)
      .gte('last_seen_season', 35)
      .order('boss_name', { ascending: true })
      .limit(1000),
    client
      .from('boss_image_db')
      .select('boss_name, boss_key, boss_image_url')
      .neq('boss_image_url', '')
      .limit(1000),
  ]);
  if (masterResult.error) throw new Error(`보스 목록을 불러오지 못했습니다: ${masterResult.error.message}`);
  return attachBossMasterDisplayImages(
    masterResult.data || [],
    legacyResult.error ? [] : legacyResult.data || [],
  );
}

export async function updateBossMasterCatalogEntry(id, updates) {
  const catalogId = String(id || '').trim();
  if (!catalogId) throw new Error('수정할 보스 식별자가 없습니다.');
  const { data, error } = await requireClient()
    .from('boss_master')
    .update(updates)
    .eq('id', catalogId)
    .select('*')
    .single();
  if (error) {
    if (error.code === '42703' || /column .* does not exist/i.test(error.message || '')) {
      throw new Error('Boss Master 확장 스키마가 아직 반영되지 않아 저장할 수 없습니다.');
    }
    throw new Error(`보스 정보를 저장하지 못했습니다: ${error.message}`);
  }
  return normalizeBossMasterCatalogRow(data);
}

export async function createManualBossMaster(name, seasonNo) {
  const row = buildManualBossMaster(name, seasonNo, crypto.randomUUID());
  const { data, error } = await requireClient().from('boss_master').insert(row).select('*').single();
  if (error) throw new Error(`보스를 추가하지 못했습니다: ${error.message}`);
  return normalizeBossMasterCatalogRow(data);
}
